/* istanbul ignore file */
import React, { useCallback, useContext } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Button,
  Caption1,
  Text,
  Textarea,
  Tooltip,
} from '@fluentui/react-components';
import { Tag, TagGroup } from '@fluentui/react-tags';
import {
  TagPicker,
  TagPickerControl,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
} from '@fluentui/react-tag-picker';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  AddRegular,
  AddSquareRegular,
  ArrowClockwiseRegular,
  InfoRegular,
  MicSparkleRegular,
  TextFontRegular,
} from '@fluentui/react-icons';
import { Taglines } from './components';
import { BrandKitContext } from './context';
import { ContentPanel } from './content-panel/index';
import { useStyles } from './styles/brand-voice-styles';
import { actionTypes } from './reducer';

export const BrandVoice = () => {
  const classes = useStyles();
  const i18n = useContext(I18nContext);
  const { state, dispatch } = useContext(BrandKitContext);

  const {
    taglines,
  } = state || {};

  const [brandVoice, setBrandVoice] = React.useState('');
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState('');
  const [mockCopilotSuggestionIndexes, setMockCopilotSuggestionIndexes] = React.useState([1, 2, 3]);

  const handleUpdateTaglines = useCallback((updatedTaglines) => {
    dispatch({
      type: actionTypes.updateTaglines,
      payload: updatedTaglines,
    });
  }, [dispatch]);

  const handleSetBrandVoice = e => setBrandVoice(e.target.value);

  const handleAddSelectedOptions = useCallback((e) => {
    if (e.key === 'Enter') {
      return;
    }
    setSelectedOptions(options => [...new Set([...options, e.target.innerText])]);
  }, []);

  const handleRemoveSelectedOption = (e, { value }) => {
    setSelectedOptions(options => [...options].filter(tag => tag !== value));
  };

  const handleChange = e => setInputValue(e.currentTarget.value);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && inputValue) {
      setSelectedOptions(options => [...new Set([...options, inputValue.trim()])]);
      setInputValue('');
    }
  }, [inputValue]);

  // TODO: Temporary, replace with real Copilot suggestions
  const mockCopilotToneSuggestions = [
    'Empathetic',
    'Inclusive',
    'Hopeful',
    'Professional',
    'Mindful',
    'Collaborative',
    'Considerate',
    'Positive',
  ];

  // TODO: Temporary, remove and replace, this was just a fun way to get some random mock suggestions
  const refreshMockCopilotSuggestions = () => {
    const numbers = [];
    while (numbers.length < 3) {
      const randomNumber = Math.floor(Math.random() * mockCopilotToneSuggestions.length);
      if (!numbers.includes(randomNumber)) {
        numbers.push(randomNumber);
      }
    }
    setMockCopilotSuggestionIndexes(numbers);
  };

  const brandPersonalityPlaceholder = i18n.getString(_TL_('Cara is a compassionate organization, deeply caring and empathetic, driven by a mission to improve healthcare for underserved communities. As a trustworthy Medical NGO, Cara is dependable and transparent, fostering trust through action and accountability'));

  const brandVoiceBody = () => (
    <div>
      <div className={classes.brandVoiceSegment}>
        <Text as="h3">{i18n.getString(_TL_('Brand Personality'))}</Text>
        <Text as="p">{i18n.getString(_TL_("How would you describe your brand's personality?"))}</Text>
        <Textarea
          className={classes.brandPersonalityTextarea}
          appearance="outline"
          placeholder={brandPersonalityPlaceholder}
          size="large"
          value={brandVoice}
          onChange={handleSetBrandVoice}
          onKeyDown={handleSetBrandVoice}
        />
      </div>
      <div className={classes.brandVoiceSegment}>
        <Text as="h3">{i18n.getString(_TL_('Brand Tone'))}</Text>
        <Text as="p">{i18n.getString(_TL_('How would you describe your brand voice in keywords?'))}</Text>
        {selectedOptions.length > 0 && (
          <div className={classes.brandPersonalityTagGroup}>
            <TagGroup aria-label="Enter keyword" onDismiss={handleRemoveSelectedOption}>
              {
                selectedOptions.length > 0 &&
                selectedOptions.map(option => (
                  <Tag
                    key={option}
                    shape="rounded"
                    value={option}
                    dismissible
                    dismissIcon={{ 'aria-label': 'remove' }}
                  >
                    {option}
                  </Tag>
                ))
              }
            </TagGroup>
          </div>
        )}
        <TagPicker
          onOptionSelect={handleAddSelectedOptions}
          selectedOptions={selectedOptions}
        >
          <TagPickerControl className={classes.brandToneInput} expandIcon={<AddSquareRegular />}>
            <TagPickerInput
              value={inputValue}
              placeholder={i18n.getString(_TL_('Enter keyword'))}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              aria-label="Enter keyword"
            />
          </TagPickerControl>
          <TagPickerList>
            {mockCopilotToneSuggestions.length ? (
              mockCopilotToneSuggestions.map(option => (
                <TagPickerOption
                  value={option}
                  key={option}
                >
                  {option}
                </TagPickerOption>
              ))
            ) : (
              <TagPickerOption value="no-options">
                {i18n.getString(_TL_('No options available'))}
              </TagPickerOption>
            )}
          </TagPickerList>
        </TagPicker>
        <Caption1 className={classes.brandToneCaption}>{i18n.getString(_TL_('Type desired tag and press enter to add'))}</Caption1>
      </div>
      <div className={classes.brandToneCaption}>
        <Accordion className={classes.brandToneAccordion} collapsible>
          <AccordionItem value="2">
            <AccordionHeader expandIconPosition="start">
              {i18n.getString(_TL_('More tone suggestions from Copilot'))}
            </AccordionHeader>
            <AccordionPanel>
              <TagGroup aria-label="Enter keyword" onDismiss={handleRemoveSelectedOption}>
                {mockCopilotSuggestionIndexes.map(index => (
                  <Tag
                    appearance="brand"
                    icon={<AddRegular />}
                    size="medium"
                  >
                    {mockCopilotToneSuggestions[index]}
                  </Tag>
                ))}
                <Tooltip content="With refresh icon only" relationship="label">
                  <Button icon={<ArrowClockwiseRegular />} onClick={refreshMockCopilotSuggestions} />
                </Tooltip>
              </TagGroup>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );

  const taglinesBody = () => (
    <Taglines
      i18n={i18n}
      taglines={taglines}
      maxAllowedTaglines={5}
      maxAllowedCharactersPerTagline={100}
      onChangeTaglines={handleUpdateTaglines}
    />
  );

  return (
    <div className="ads-studio-brand-voice">
      <ContentPanel
        title={i18n.getString(_TL_('Brand Voice'))}
        isLoading={false}
        headerProps={{ contentBefore: <MicSparkleRegular />, contentAfter: <InfoRegular className="info_icon" /> }}
        bodyContent={brandVoiceBody()}
      />
      <ContentPanel
        title={i18n.getString(_TL_('Taglines'))}
        isLoading={false}
        headerProps={{ contentBefore: <TextFontRegular />, contentAfter: <InfoRegular className="info_icon" /> }}
        bodyContent={taglinesBody()}
      />
    </div>
  );
};
